/**
 * PersonaRename Component
 * =======================
 * Allows users to rename their Operator's display persona.
 * This is display-only and does not affect internal IDs.
 * 
 * The persona_name is used in:
 * - UI header display
 * - Chat messages (Telegram, WhatsApp)
 * - Suggestion cards
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';
import { fetchWithOrg } from '../../utils/api';

import {
  Check,
  CircleNotch,
  PencilSimple,
  User,
  X
} from '@phosphor-icons/react';
export const PersonaRename = ({ agentId, currentName, onNameChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(currentName || 'Orion');
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    const trimmedName = newName.trim();
    
    if (!trimmedName) {
      toast.error('Persona name cannot be empty');
      return;
    }
    
    if (trimmedName.length > 50) {
      toast.error('Persona name must be 50 characters or less');
      return;
    }
    
    if (trimmedName === currentName) {
      setIsEditing(false);
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${agentId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ persona_name: trimmedName })
        }
      );
      
      if (response.ok) {
        toast.success(`Persona renamed to "${trimmedName}"`);
        onNameChange?.(trimmedName);
        setIsEditing(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to rename persona');
      }
    } catch (err) {
      console.error('[PERSONA] Rename error:', err);
      toast.error('Failed to rename persona');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    setNewName(currentName || 'Orion');
    setIsEditing(false);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  
  return (
    <div className="flex items-center gap-2" data-testid="persona-rename-container">
      {isEditing ? (
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="px-3 py-1.5 text-sm rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:outline-none focus:border-[#00E5C0]/50 w-40"
            placeholder="Enter persona name"
            maxLength={50}
            autoFocus
            disabled={saving}
            data-testid="persona-rename-input"
          />
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="p-1.5 rounded-lg bg-green-500/20 text-[#00E5C0] hover:bg-green-500/30 disabled:opacity-50 transition-all"
            data-testid="persona-rename-save"
          >
            {saving ? (
              <CircleNotch size={16} className="animate-spin" />
            ) : (
              <Check size={16} />
            )}
          </button>
          
          <button
            onClick={handleCancel}
            disabled={saving}
            className="p-1.5 rounded-lg bg-red-500/20 text-[#FF6B6B] hover:bg-red-500/30 disabled:opacity-50 transition-all"
            data-testid="persona-rename-cancel"
          >
            <X size={16} />
          </button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2 group"
        >
          <span className="text-white font-medium" data-testid="persona-name-display">
            {currentName || 'Orion'}
          </span>
          
          <button
            onClick={() => setIsEditing(true)}
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/5 transition-all"
            title="Rename persona"
            data-testid="persona-rename-edit-btn"
          >
            <PencilSimple size={14} className="text-[#A0AEC0]" />
          </button>
        </motion.div>
      )}
    </div>
  );
};

/**
 * PersonaSettings Component
 * =========================
 * Full settings panel for persona configuration.
 * Used in the Settings tab.
 */
export const PersonaSettings = ({ agentId, currentName, onNameChange }) => {
  return (
    <div className="glass rounded-xl p-5" data-testid="persona-settings-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-[#00E5C0]/20">
          <User size={20} className="text-[#00E5C0]" />
        </div>
        <div>
          <h3 className="text-white font-medium">Persona Settings</h3>
          <p className="text-[#A0AEC0] text-sm">Customize how your Operator identifies itself</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-[#A0AEC0] mb-2">
            Display Name
          </label>
          <p className="text-xs text-[#A0AEC0]/60 mb-2">
            This name appears in chat messages and the UI. Does not affect internal identifiers.
          </p>
          <PersonaRename 
            agentId={agentId} 
            currentName={currentName} 
            onNameChange={onNameChange}
          />
        </div>
        
        <div className="pt-3 border-t border-white/10">
          <p className="text-xs text-[#A0AEC0]/60">
            💡 Tip: Your Operator will use this name when sending messages via Telegram, WhatsApp, and in suggestions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PersonaRename;
